import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { TFunction } from "i18next";
import { tasksApiService, OpenTask } from "../services/tasks.service";
import { useCart } from "./CartContext";

const POLL_INTERVAL_MS = 30 * 60 * 1000; // 30 minutes
const ONE_SHOT_DELAY_MS = 3.5 * 60 * 1000; // 3.5 minutes

interface TasksContextType {
  tasks: OpenTask[];
  tasksLoading: boolean;
  scheduleOneShotPoll: () => void;
}

const TasksContext = createContext<TasksContextType | undefined>(undefined);

export function getTaskStatusLabel(statusName: string, t: TFunction): string {
  return statusName.toLowerCase() === "new"
    ? t("taskStatus.waitingForProcessing")
    : t("taskStatus.inProgress");
}

export const TasksProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { otpReady, businessInfo } = useCart();
  const [tasks, setTasks] = useState<OpenTask[]>([]);
  const [tasksLoading, setTasksLoading] = useState(false);

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const oneShotTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchTasks = useCallback(async (haatBussID: string) => {
    setTasksLoading(true);
    try {
      const result = await tasksApiService.getOpenTasks(haatBussID);
      setTasks(result);
    } finally {
      setTasksLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!otpReady || !businessInfo?.haatBussID) {
      setTasks([]);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    const haatBussID = businessInfo.haatBussID;

    fetchTasks(haatBussID);

    intervalRef.current = setInterval(() => {
      fetchTasks(haatBussID);
    }, POLL_INTERVAL_MS);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [otpReady, businessInfo?.haatBussID, fetchTasks]);

  const scheduleOneShotPoll = useCallback(() => {
    if (!businessInfo?.haatBussID) return;
    if (oneShotTimerRef.current) {
      clearTimeout(oneShotTimerRef.current);
    }
    const haatBussID = businessInfo.haatBussID;
    oneShotTimerRef.current = setTimeout(() => {
      fetchTasks(haatBussID);
      oneShotTimerRef.current = null;
    }, ONE_SHOT_DELAY_MS);
  }, [businessInfo?.haatBussID, fetchTasks]);

  useEffect(() => {
    return () => {
      if (oneShotTimerRef.current) {
        clearTimeout(oneShotTimerRef.current);
      }
    };
  }, []);

  return (
    <TasksContext.Provider value={{ tasks, tasksLoading, scheduleOneShotPoll }}>
      {children}
    </TasksContext.Provider>
  );
};

export const useTasks = (): TasksContextType => {
  const context = useContext(TasksContext);
  if (context === undefined) {
    throw new Error("useTasks must be used within a TasksProvider");
  }
  return context;
};

export default TasksContext;
